import { streamingFieldText } from "../../i18n/streamingFeedbackPresentation";
import { stText, useStandaloneLanguage, stFeedback, stMessage } from "../../i18n/streamingPagesPresentation";
/**
 * TemplateManager -- Full-featured template editor for WSD user account templates.
 *
 * Allows admins to create a new template from scratch or edit an existing one
 * by toggling individual fields across categories. Each field has an "include"
 * switch (whether the field is part of the template) and a value control
 * (what value to set when the template is applied).
 *
 * Visual treatment matches ControlsTab with accent left-border stripes,
 * SectionHeader with tinted icon backgrounds, and dense dashboard spacing.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { CCTooltip } from "../ui/CCTooltip";
import { Icon } from "../ui/Icon";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Spinner } from "../ui/Spinner";
import { Switch } from "../ui/Switch";
import { TagPicker } from "../ui/TagPicker";
import { Textarea } from "../ui/Textarea";
import { cn } from "../../utils/cn";
import { toast } from "../../lib/toast";
import { LibraryPickerPopover } from "./user-admin/LibraryPickerPopover";
import {
  useCreateTemplate,
  useUpdateTemplate,
  type WsdUserTemplate,
  type TemplatePolicyFields,
  type TemplateConfigFields,
  type TemplateWsdLocalFields,
} from "../../hooks/useUserTemplates";
import {
  TEMPLATE_CATEGORIES,
  type FieldBucket,
  type FieldCategory,
  type FieldDef,
} from "./template-fields";

// ============================================================================
// Types
// ============================================================================

/** Library shape with optional sub-folder tree (for LibraryPickerPopover) */
type AvailableLibrary = {
  id: string;
  name: string;
  subFolders?: Array<{ compositeId: string; path: string }>;
};

export type TemplateManagerProps = {
  /** Template to edit (null = create new) */
  template?: WsdUserTemplate | null;
  /** Called when save completes */
  onSave?: (template: WsdUserTemplate) => void;
  /** Called when cancel/close */
  onClose?: () => void;
  /** Available server libraries for the Library Access picker */
  availableLibraries?: AvailableLibrary[];
  /** Server type for the current context (controls sub-folder visibility) */
  serverType?: string;
};

// ============================================================================
// Accent system (matches ControlsTab)
// ============================================================================

type AccentColor = FieldCategory["accent"];

const accentMap: Record<AccentColor, { border: string; iconBg: string; iconColor: string }> = {
  amber: { border: "border-l-amber-500", iconBg: "bg-amber-500/10", iconColor: "text-amber-400" },
  sky: { border: "border-l-sky-500", iconBg: "bg-sky-500/10", iconColor: "text-sky-400" },
  violet: { border: "border-l-violet-500", iconBg: "bg-violet-500/10", iconColor: "text-violet-400" },
  rose: { border: "border-l-rose-500", iconBg: "bg-rose-500/10", iconColor: "text-rose-400" },
  slate: { border: "border-l-slate-500", iconBg: "bg-muted/20", iconColor: "text-muted-foreground" },
  emerald: { border: "border-l-emerald-500", iconBg: "bg-emerald-500/10", iconColor: "text-emerald-400" },
  cyan: { border: "border-l-cyan-500", iconBg: "bg-cyan-500/10", iconColor: "text-cyan-400" },
  pink: { border: "border-l-pink-500", iconBg: "bg-pink-500/10", iconColor: "text-pink-400" },
  indigo: { border: "border-l-indigo-500", iconBg: "bg-indigo-500/10", iconColor: "text-indigo-400" },
  teal: { border: "border-l-teal-500", iconBg: "bg-teal-500/10", iconColor: "text-teal-400" },
  orange: { border: "border-l-orange-500", iconBg: "bg-orange-500/10", iconColor: "text-orange-400" },
};

// ============================================================================
// Constants
// ============================================================================

/** localStorage key to persist the template hint banner dismissed state */
const TEMPLATE_HINT_DISMISSED_KEY = "wsd_template_hint_dismissed";

/** Safely read dismissed state from localStorage */
function isHintDismissed(): boolean {
  try {
    return localStorage.getItem(TEMPLATE_HINT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

// ============================================================================
// Helpers
// ============================================================================

/** Build a composite key for the includedFields set */
function fieldKey(bucket: FieldBucket, key: string): string {
  return `${bucket}:${key}`;
}

/**
 * Set of valid field keys (`bucket:key`) derived from TEMPLATE_CATEGORIES.
 * Used to filter out unknown API fields that the backend stored but the Zod
 * schema does not accept on update.  Computed once at module level.
 */
const VALID_FIELD_KEYS = new Set<string>(
  TEMPLATE_CATEGORIES.flatMap((cat) =>
    cat.fields.map((f) => fieldKey(f.bucket, f.key)),
  ),
);

/** Initialize state from an existing template */
function initFromTemplate(template: WsdUserTemplate): {
  included: Set<string>;
  values: Record<string, unknown>;
} {
  const included = new Set<string>();
  const values: Record<string, unknown> = {};

  const buckets: Array<{ bucket: FieldBucket; data: Record<string, unknown> }> = [
    { bucket: "policy", data: template.policyFields as Record<string, unknown> },
    { bucket: "config", data: template.configFields as Record<string, unknown> },
    { bucket: "wsdLocal", data: template.wsdLocalFields as Record<string, unknown> },
  ];

  for (const { bucket, data } of buckets) {
    for (const [k, v] of Object.entries(data)) {
      if (v !== undefined) {
        const fk = fieldKey(bucket, k);
        // Only include fields that have a matching FieldDef in TEMPLATE_CATEGORIES.
        // Stored templates may contain extra API fields (e.g. from Emby/Jellyfin
        // user imports) that the backend Zod schema rejects on update.
        if (VALID_FIELD_KEYS.has(fk)) {
          included.add(fk);
          values[fk] = v;
        }
      }
    }
  }

  return { included, values };
}

// ============================================================================
// Sub-components
// ============================================================================

/** Section header with tinted icon badge (matches ControlsTab pattern) */
const SectionHeader = ({
  icon,
  accent,
  children,
  fieldCount,
  includedCount,
}: {
  icon: string;
  accent: AccentColor;
  children: React.ReactNode;
  fieldCount: number;
  includedCount: number;
}) => {
  useStandaloneLanguage();
  const a = accentMap[accent];
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <div className={cn("rounded-md flex items-center justify-center h-5 w-5 shrink-0", a.iconBg)}>
        <Icon icon={icon} className={cn("h-3 w-3", a.iconColor)} />
      </div>
      <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {children}
      </h4>
      {includedCount > 0 && (
        <Badge variant="primary" size="xs">
          {includedCount}/{fieldCount}
        </Badge>
      )}
    </div>
  );
};

/** Value control for a field based on its type */
const ValueControl = ({
  field,
  value,
  onChange,
  disabled,
  suggestions,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
  suggestions?: string[];
}) => {
  useStandaloneLanguage();
  switch (field.type) {
    case "boolean":
      return (
        <Switch
          checked={Boolean(value)}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          aria-label={stText("labelValue", { label: field.label })}
        />
      );

    case "number":
      return (
        <div className="flex items-center gap-1.5">
          <Input
            size="compact"
            type="number"
            min={field.min ?? 0}
            max={field.max}
            value={typeof value === "number" ? value : 0}
            onChange={(e) => onChange(Math.max(field.min ?? 0, parseInt(e.target.value, 10) || 0))}
            disabled={disabled}
            className="w-20 text-right tabular-nums"
            aria-label={stText("labelValue", { label: field.label })}
          />
          {field.unit && (
            <span className="text-[10px] 2xl:text-xs text-muted-foreground whitespace-nowrap">
              {field.unit}
            </span>
          )}
        </div>
      );

    case "string":
      return (
        <Input
          size="compact"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.description}
          className="w-36 text-xs"
          aria-label={stText("labelValue", { label: field.label })}
        />
      );

    case "string[]":
      return (
        <div className="w-full">
          <TagPicker
            value={Array.isArray(value) ? value as string[] : []}
            onChange={(v) => onChange(v)}
            disabled={disabled}
            placeholder={stText("selectLabel", { label: field.label.toLowerCase() })}
            aria-label={stText("labelValues", { label: field.label })}
            allowCreate
            suggestions={suggestions}
          />
        </div>
      );

    case "enum":
      return (
        <Select
          size="compact"
          value={typeof value === "string" ? value : String(field.defaultValue)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={stText("labelValue", { label: field.label })}
          className="w-36"
        >
          {(field.enumOptions ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      );

    default:
      return null;
  }
};

/** Compact field tile for boolean fields in mosaic grid (matches ControlsTab ToggleTile) */
const CompactFieldTile = ({
  field,
  included,
  value,
  onToggleInclude,
  onValueChange,
  disabled,
  suggestions,
}: {
  field: FieldDef;
  included: boolean;
  value: unknown;
  onToggleInclude: () => void;
  onValueChange: (v: unknown) => void;
  disabled: boolean;
  suggestions?: string[];
}) => { useStandaloneLanguage(); return (
  <div
    className={cn(
      "flex items-center justify-between gap-2 py-1.5 px-2 rounded-md transition-colors min-w-0 overflow-hidden",
      included ? "hover:bg-muted/30" : "hover:bg-muted/15",
    )}
  >
    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
      {/* Include checkbox */}
      <label className="flex items-center shrink-0 cursor-pointer">
        <input
          type="checkbox"
          checked={included}
          onChange={onToggleInclude}
          disabled={disabled}
          aria-label={stText("includeLabelInTemplate", { label: field.label })}
          className="h-3.5 w-3.5 rounded border-border bg-background text-primary accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        />
      </label>
      {/* Status dot */}
      {field.type === "boolean" && included && (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full shrink-0 transition-colors",
            Boolean(value) ? "bg-emerald-500" : "bg-muted-foreground/25",
          )}
          aria-hidden="true"
        />
      )}
      <CCTooltip label={field.label} description={field.description} serverOnly={field.serverOnly}>
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={cn(
              "text-xs 2xl:text-sm truncate block transition-colors",
              included ? "text-foreground font-medium" : "text-muted-foreground",
            )}
          >
            {field.label}
          </span>
          {field.serverOnly && (
            <Badge
              variant={field.serverOnly === "emby" ? "success" : "muted"}
              size="xs"
              className="capitalize shrink-0"
            >
              {field.serverOnly}
            </Badge>
          )}
        </div>
        <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5 truncate block max-w-full">
          {field.description}
        </p>
      </CCTooltip>
    </div>
    {/* Value control */}
    {included && (
      <div className="shrink-0">
        <ValueControl
          field={field}
          value={value}
          onChange={onValueChange}
          disabled={disabled}
          suggestions={suggestions}
        />
      </div>
    )}
  </div>
); };

/** Wide field row for string[] (tag pickers) — full width below mosaic grid */
const WideFieldRow = ({
  field,
  included,
  value,
  onToggleInclude,
  onValueChange,
  disabled,
  suggestions,
}: {
  field: FieldDef;
  included: boolean;
  value: unknown;
  onToggleInclude: () => void;
  onValueChange: (v: unknown) => void;
  disabled: boolean;
  suggestions?: string[];
}) => { useStandaloneLanguage(); return (
  <div className="py-1.5 px-2">
    <div className="flex items-center gap-2 mb-1.5">
      <label className="flex items-center shrink-0 cursor-pointer">
        <input
          type="checkbox"
          checked={included}
          onChange={onToggleInclude}
          disabled={disabled}
          aria-label={stText("includeLabelInTemplate", { label: field.label })}
          className="h-3.5 w-3.5 rounded border-border bg-background text-primary accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        />
      </label>
      <span
        className={cn(
          "text-xs 2xl:text-sm transition-colors",
          included ? "text-foreground font-medium" : "text-muted-foreground",
        )}
      >
        {field.label}
      </span>
      {field.serverOnly && (
        <Badge
          variant={field.serverOnly === "emby" ? "success" : "muted"}
          size="xs"
          className="capitalize shrink-0"
        >
          {field.serverOnly}
        </Badge>
      )}
      {included && Array.isArray(value) && (
        <span className="text-[10px] 2xl:text-xs text-muted-foreground ml-auto">{stText("inline7", { count: (value as string[]).length, value0: (value as string[]).length })}</span>
      )}
    </div>
    {included && (
      <div className="ml-6">
        <ValueControl
          field={field}
          value={value}
          onChange={onValueChange}
          disabled={disabled}
          suggestions={suggestions}
        />
      </div>
    )}
    <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5 ml-6">
      {field.description}
    </p>
  </div>
); };

/** A collapsible category card with mosaic grid layout (matches ControlsTab) */
const CategorySection = ({
  category,
  includedFields,
  fieldValues,
  onToggleField,
  onChangeValue,
  disabled,
  serverType,
  halfWidth = false,
  availableLibraries,
  contextServerType,
}: {
  category: FieldCategory;
  includedFields: Set<string>;
  fieldValues: Record<string, unknown>;
  onToggleField: (fk: string, field: FieldDef) => void;
  onChangeValue: (fk: string, value: unknown) => void;
  disabled: boolean;
  serverType: string | null;
  halfWidth?: boolean;
  availableLibraries?: AvailableLibrary[];
  /** Actual server type for LibraryPickerPopover (emby/jellyfin) */
  contextServerType?: string;
}) => {
  useStandaloneLanguage();
  const [collapsed, setCollapsed] = useState(false);

  // Filter fields by server type
  const visibleFields = category.fields.filter(
    (f) => !f.serverOnly || !serverType || f.serverOnly === serverType || serverType === "both",
  );

  const includedCount = visibleFields.filter((f) =>
    includedFields.has(fieldKey(f.bucket, f.key)),
  ).length;

  if (visibleFields.length === 0) return null;

  // Split into compact (can share grid columns) vs wide (need full width — tag pickers)
  const compactFields = visibleFields.filter((f) => f.type !== "string[]");
  const wideFields = visibleFields.filter((f) => f.type === "string[]");

  // Smart column count — context-aware mosaic (matches ControlsTab logic)
  const gridColClass = halfWidth
    ? (compactFields.length === 1
        ? "grid-cols-1"
        : "grid-cols-1 sm:grid-cols-2")
    : (compactFields.length === 1
        ? "grid-cols-1"
        : compactFields.length === 2
          ? "grid-cols-1 sm:grid-cols-2"
          : compactFields.length === 3
            ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
            : compactFields.length === 4
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3");

  const a = accentMap[category.accent];

  return (
    <div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setCollapsed((p) => !p)}
          className="flex items-center gap-0.5 text-muted-foreground/80 hover:text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 rounded-sm p-0.5"
          aria-expanded={!collapsed}
          aria-controls={`category-${category.id}`}
        >
          <Icon
            icon="lucide:chevron-right"
            className={cn("h-3 w-3 transition-transform", !collapsed && "rotate-90")}
          />
        </button>
        <SectionHeader
          icon={category.icon}
          accent={category.accent}
          fieldCount={visibleFields.length}
          includedCount={includedCount}
        >
          {category.title}
        </SectionHeader>
      </div>
      {!collapsed && (
        <div
          id={`category-${category.id}`}
          className={cn(
            "rounded-lg border border-border/50 bg-card",
            "border-l-2",
            a.border,
          )}
        >
          {/* Compact fields in mosaic grid */}
          {compactFields.length > 0 && (
            <div className={cn("grid gap-x-2 gap-y-0", gridColClass)}>
              {compactFields.map((field) => {
                const fk = fieldKey(field.bucket, field.key);
                return (
                  <CompactFieldTile
                    key={fk}
                    field={field}
                    included={includedFields.has(fk)}
                    value={fieldValues[fk]}
                    onToggleInclude={() => onToggleField(fk, field)}
                    onValueChange={(v) => onChangeValue(fk, v)}
                    disabled={disabled}
                    suggestions={field.key === "EnabledFolderNames" ? availableLibraries?.map((l) => l.name) : undefined}
                  />
                );
              })}
            </div>
          )}

          {/* Library category: unified hierarchical picker for EnabledFolderNames + ExcludedSubFolders */}
          {category.id === "library" && availableLibraries && availableLibraries.length > 0 && (() => {
            const folderField = wideFields.find((f) => f.key === "EnabledFolderNames");
            const excludedField = wideFields.find((f) => f.key === "ExcludedSubFolders");
            const nonLibraryWideFields = wideFields.filter(
              (f) => f.key !== "EnabledFolderNames" && f.key !== "ExcludedSubFolders",
            );
            const folderFk = folderField ? fieldKey(folderField.bucket, folderField.key) : "";
            const excludedFk = excludedField ? fieldKey(excludedField.bucket, excludedField.key) : "";
            const folderIncluded = folderField ? includedFields.has(folderFk) : false;
            const excludedIncluded = excludedField ? includedFields.has(excludedFk) : false;
            const allFoldersField = compactFields.find((f) => f.key === "EnableAllFolders");
            const allFoldersFk = allFoldersField ? fieldKey(allFoldersField.bucket, allFoldersField.key) : "";
            const allFoldersEnabled = allFoldersField
              ? includedFields.has(allFoldersFk) && Boolean(fieldValues[allFoldersFk])
              : false;

            // Resolve enabledFolderIds: stored as names, convert to IDs
            const normalize = (id: string) => id.replace(/-/g, "").toLowerCase();
            const nameToId = new Map(availableLibraries.map((l) => [l.name, l.id]));
            const idSet = new Set(availableLibraries.map((l) => normalize(l.id)));
            const storedNames = Array.isArray(fieldValues[folderFk]) ? fieldValues[folderFk] as string[] : [];
            const enabledFolderIds = storedNames.map((v) => {
              // If it's already an ID, use it directly
              if (idSet.has(normalize(v))) return v;
              // Otherwise resolve name to ID
              return nameToId.get(v) ?? v;
            });

            const excludedSubFolderIds = Array.isArray(fieldValues[excludedFk]) ? fieldValues[excludedFk] as string[] : [];

            // Build libraries array with subFolders for the picker
            const pickerLibraries = availableLibraries.map((lib) => ({
              id: lib.id,
              name: lib.name,
              subFolders: lib.subFolders ?? [],
            }));

            return (
              <>
                {(folderField || excludedField) && (
                  <div className={cn(compactFields.length > 0 && "border-t border-border/30", "py-1.5 px-2")}>
                    {/* Include checkboxes for template fields */}
                    <div className="flex items-center gap-3 mb-1.5">
                      {folderField && (
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={folderIncluded}
                            onChange={() => onToggleField(folderFk, folderField)}
                            disabled={disabled}
                            aria-label={stText("includeAllowedLibrariesInTemplate")}
                            className="h-3.5 w-3.5 rounded border-border bg-background text-primary accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <span className={cn(
                            "text-xs 2xl:text-sm transition-colors",
                            folderIncluded ? "text-foreground font-medium" : "text-muted-foreground",
                          )}>
                            {stText("allowedLibraries")}</span>
                        </label>
                      )}
                      {excludedField && (!excludedField.serverOnly || !serverType || excludedField.serverOnly === serverType || serverType === "both") && (
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={excludedIncluded}
                            onChange={() => onToggleField(excludedFk, excludedField)}
                            disabled={disabled}
                            aria-label={stText("includeExcludedSubFoldersInTemplate")}
                            className="h-3.5 w-3.5 rounded border-border bg-background text-primary accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <span className={cn(
                            "text-xs 2xl:text-sm transition-colors",
                            excludedIncluded ? "text-foreground font-medium" : "text-muted-foreground",
                          )}>
                            {stText("excludedSubFolders")}</span>
                          <Badge variant="success" size="xs" className="shrink-0">emby</Badge>
                        </label>
                      )}
                    </div>
                    {/* Unified library picker (shown when either field is included) */}
                    {(folderIncluded || excludedIncluded) && (
                      <LibraryPickerPopover
                        libraries={pickerLibraries}
                        enabledFolderIds={enabledFolderIds}
                        excludedSubFolderIds={excludedSubFolderIds}
                        allFoldersEnabled={allFoldersEnabled}
                        serverType={contextServerType ?? "emby"}
                        disabled={disabled}
                        onLibraryChange={(ids) => {
                          // Resolve IDs to names for template storage
                          const idToName = new Map(availableLibraries.map((l) => [normalize(l.id), l.name]));
                          const names = ids.map((id) => idToName.get(normalize(id)) ?? id);
                          onChangeValue(folderFk, names);
                          // Auto-include the field
                          if (folderField && !folderIncluded) {
                            onToggleField(folderFk, folderField);
                          }
                        }}
                        onSubFolderChange={(ids) => {
                          onChangeValue(excludedFk, ids);
                          // Auto-include the field when exclusions are added
                          if (excludedField && !excludedIncluded && ids.length > 0) {
                            onToggleField(excludedFk, excludedField);
                          }
                        }}
                      />
                    )}
                    {!folderIncluded && !excludedIncluded && (
                      <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5">
                        {stText("checkAFieldAboveToConfigureLibraryAccessFor")}</p>
                    )}
                  </div>
                )}
                {/* Non-library wide fields (if any remain) */}
                {nonLibraryWideFields.length > 0 && (
                  <div className={cn((compactFields.length > 0 || folderField || excludedField) && "border-t border-border/30")}>
                    {nonLibraryWideFields.map((field) => {
                      const fk = fieldKey(field.bucket, field.key);
                      return (
                        <WideFieldRow
                          key={fk}
                          field={field}
                          included={includedFields.has(fk)}
                          value={fieldValues[fk]}
                          onToggleInclude={() => onToggleField(fk, field)}
                          onValueChange={(v) => onChangeValue(fk, v)}
                          disabled={disabled}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

          {/* Wide fields (tag pickers) below compact grid -- non-library categories */}
          {category.id !== "library" && wideFields.length > 0 && (
            <div className={cn(compactFields.length > 0 && "border-t border-border/30")}>
              {wideFields.map((field) => {
                const fk = fieldKey(field.bucket, field.key);
                return (
                  <WideFieldRow
                    key={fk}
                    field={field}
                    included={includedFields.has(fk)}
                    value={fieldValues[fk]}
                    onToggleInclude={() => onToggleField(fk, field)}
                    onValueChange={(v) => onChangeValue(fk, v)}
                    disabled={disabled}
                  />
                );
              })}
            </div>
          )}

          {/* Fallback: library category without availableLibraries -- use tag pickers */}
          {category.id === "library" && (!availableLibraries || availableLibraries.length === 0) && wideFields.length > 0 && (
            <div className={cn(compactFields.length > 0 && "border-t border-border/30")}>
              {wideFields.map((field) => {
                const fk = fieldKey(field.bucket, field.key);
                return (
                  <WideFieldRow
                    key={fk}
                    field={field}
                    included={includedFields.has(fk)}
                    value={fieldValues[fk]}
                    onToggleInclude={() => onToggleField(fk, field)}
                    onValueChange={(v) => onChangeValue(fk, v)}
                    disabled={disabled}
                    suggestions={field.key === "EnabledFolderNames" ? availableLibraries?.map((l) => l.name) : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const TemplateManager = ({ template, onSave, onClose, availableLibraries, serverType: contextServerType }: TemplateManagerProps) => {
  useStandaloneLanguage();
  const isEditing = template != null;

  // -- Header fields
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [serverType, setServerType] = useState<string>(template?.serverType ?? "both");
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false);

  // -- Dismissable hint banner
  const [hintDismissed, setHintDismissed] = useState(isHintDismissed);
  const dismissHint = useCallback(() => {
    setHintDismissed(true);
    try { localStorage.setItem(TEMPLATE_HINT_DISMISSED_KEY, "1"); } catch { /* ignore */ }
  }, []);

  // -- Field state
  const [includedFields, setIncludedFields] = useState<Set<string>>(new Set());
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});

  // -- Hooks
  const { creating, createTemplate } = useCreateTemplate();
  const { updating, updateTemplate } = useUpdateTemplate();
  const isBusy = creating || updating;

  // Initialize from template when editing
  useEffect(() => {
    if (template) {
      const { included, values } = initFromTemplate(template);

      // Resolve any raw IDs in EnabledFolderNames to human-readable names.
      // Templates may store IDs (GUIDs) if imported before name resolution,
      // or may contain raw EnabledFolders IDs that need migration.
      if (availableLibraries && availableLibraries.length > 0) {
        const normalize = (id: string) => id.replace(/-/g, "").toLowerCase();
        const idToName = new Map(availableLibraries.map((l) => [normalize(l.id), l.name]));
        const nameSet = new Set(availableLibraries.map((l) => l.name));

        // If template has raw EnabledFolders (IDs), migrate to EnabledFolderNames
        const rawKey = fieldKey("policy", "EnabledFolders");
        const folderKey = fieldKey("policy", "EnabledFolderNames");
        if (Array.isArray(values[rawKey]) && !values[folderKey]) {
          values[folderKey] = values[rawKey];
          included.add(folderKey);
          included.delete(rawKey);
          delete values[rawKey];
        }

        // Resolve any values that look like IDs (not matching known library names)
        const folderVal = values[folderKey];
        if (Array.isArray(folderVal) && folderVal.length > 0) {
          const resolved = (folderVal as string[]).map((v) => {
            if (nameSet.has(v)) return v; // Already a name
            return idToName.get(normalize(v)) ?? v; // Try ID→name resolution
          });
          values[folderKey] = resolved;
        }
      }

      setIncludedFields(included);
      setFieldValues(values);
      setName(template.name);
      setDescription(template.description ?? "");
      setServerType(template.serverType ?? "both");
      setIsDefault(template.isDefault);
    }
  }, [template, availableLibraries]);

  // -- Toggle a field's inclusion
  const handleToggleField = useCallback((fk: string, field: FieldDef) => {
    setIncludedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fk)) {
        next.delete(fk);
      } else {
        next.add(fk);
        // Set default value when first included
        setFieldValues((vals) => {
          if (vals[fk] === undefined) {
            return { ...vals, [fk]: field.defaultValue };
          }
          return vals;
        });
      }
      return next;
    });
  }, []);

  // -- Change a field's value
  const handleChangeValue = useCallback((fk: string, value: unknown) => {
    setFieldValues((prev) => ({ ...prev, [fk]: value }));
  }, []);

  // -- Count total included fields
  const totalIncluded = includedFields.size;

  // -- Effective server type for filtering (null means "both")
  const effectiveServerType = serverType === "both" ? null : serverType;

  // -- Build payload and save
  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toast.error(stText("templateNameIsRequired"));
      return;
    }

    // Build field payloads from included fields
    const policyFields: Record<string, unknown> = {};
    const configFields: Record<string, unknown> = {};
    const wsdLocalFields: Record<string, unknown> = {};

    for (const fk of includedFields) {
      const [bucket, ...keyParts] = fk.split(":");
      const key = keyParts.join(":");
      const value = fieldValues[fk];

      switch (bucket) {
        case "policy":
          policyFields[key] = value;
          break;
        case "config":
          configFields[key] = value;
          break;
        case "wsdLocal":
          wsdLocalFields[key] = value;
          break;
      }
    }

    try {
      let result: WsdUserTemplate | null;

      if (isEditing && template) {
        result = await updateTemplate(template.id, {
          name: name.trim(),
          description: description.trim() || null,
          serverType: effectiveServerType,
          isDefault,
          policyFields: policyFields as TemplatePolicyFields,
          configFields: configFields as TemplateConfigFields,
          wsdLocalFields: wsdLocalFields as TemplateWsdLocalFields,
        });
        toast.success(stText("templateUpdated"));
      } else {
        result = await createTemplate({
          name: name.trim(),
          description: description.trim() || undefined,
          serverType: effectiveServerType,
          isDefault,
          policyFields: policyFields as TemplatePolicyFields,
          configFields: configFields as TemplateConfigFields,
          wsdLocalFields: wsdLocalFields as TemplateWsdLocalFields,
        });
        toast.success(stText("templateCreated"));
      }

      if (result) {
        onSave?.(result);
      }
    } catch (err) {
      toast.error(stMessage(stFeedback(err, "failedToSaveTemplate")) ?? "");
    }
  }, [
    name, description, effectiveServerType, isDefault,
    includedFields, fieldValues, isEditing, template,
    createTemplate, updateTemplate, onSave,
  ]);

  // Compute categories that have visible fields
  const visibleCategories = useMemo(
    () =>
      TEMPLATE_CATEGORIES.filter((cat) =>
        cat.fields.some(
          (f) =>
            !f.serverOnly ||
            !effectiveServerType ||
            f.serverOnly === effectiveServerType,
        ),
      ),
    [effectiveServerType],
  );

  // Build 2-column mosaic layout (matches ControlsTab pattern)
  // Large categories (5+ visible fields) get full width; smaller ones pair side-by-side
  const layout = useMemo(() => {
    const items: Array<{ categories: FieldCategory[]; span: "full" | "half" }> = [];
    let pendingHalf: FieldCategory | null = null;

    for (const cat of visibleCategories) {
      const visibleCount = cat.fields.filter(
        (f) => !f.serverOnly || !effectiveServerType || f.serverOnly === effectiveServerType,
      ).length;

      if (visibleCount >= 5) {
        if (pendingHalf) {
          items.push({ categories: [pendingHalf], span: "half" });
          pendingHalf = null;
        }
        items.push({ categories: [cat], span: "full" });
      } else {
        if (pendingHalf) {
          items.push({ categories: [pendingHalf, cat], span: "half" });
          pendingHalf = null;
        } else {
          pendingHalf = cat;
        }
      }
    }
    if (pendingHalf) {
      items.push({ categories: [pendingHalf], span: "half" });
    }

    return items;
  }, [visibleCategories, effectiveServerType]);

  return (
    <div className="space-y-3">
      {/* Header card with title + meta fields + actions */}
      <div className="rounded-lg border border-border/50 bg-card border-l-2 border-l-primary/60 p-3 space-y-3">
        {/* Title row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="rounded-md flex items-center justify-center h-5 w-5 shrink-0 bg-primary/10">
              <Icon
                icon={isEditing ? "lucide:pencil" : "lucide:plus"}
                className="h-3 w-3 text-primary-text"
              />
            </div>
            <h3 className="text-xs 2xl:text-sm font-semibold text-foreground truncate">
              {isEditing ? stText("editTemplateName", { name: template.name }) : stText("newTemplate")}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onClose && (
              <Button
                variant="secondary"
                size="sm"
                className="h-7 text-xs"
                onClick={onClose}
                disabled={isBusy}
              >
                {stText("cancel")}</Button>
            )}
            <Button
              variant="primary"
              size="sm"
              className="h-7 text-xs"
              onClick={() => void handleSave()}
              disabled={isBusy || !name.trim()}
            >
              {isBusy ? (
                <Spinner size="3" className="mr-1.5" />
              ) : (
                <Icon icon="lucide:save" className="h-3 w-3 mr-1.5" />
              )}
              {isEditing ? stText("update") : stText("create")}
            </Button>
          </div>
        </div>

        {/* Meta fields */}
        <div className="border-t border-border/20 pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] 2xl:text-xs font-medium uppercase tracking-wide text-muted-foreground block mb-1">
                {stText("templateName")}<span className="text-destructive">*</span>
              </label>
              <Input
                size="compact"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={stText("eGStandardUserRestrictedAccess")}
                disabled={isBusy}
                autoFocus={!isEditing}
              />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-[10px] 2xl:text-xs font-medium uppercase tracking-wide text-muted-foreground block mb-1">
                  {stText("serverType")}</label>
                <Select
                  size="compact"
                  value={serverType}
                  onChange={(e) => setServerType(e.target.value)}
                  disabled={isBusy}
                >
                  <option value="both">{stText("bothEmbyJellyfin")}</option>
                  <option value="emby">{stText("embyOnly")}</option>
                  <option value="jellyfin">{stText("jellyfinOnly")}</option>
                </Select>
              </div>
              <div className="flex items-center gap-2 pb-0.5">
                <Switch
                  checked={isDefault}
                  onChange={setIsDefault}
                  disabled={isBusy}
                  aria-label={stText("setAsDefaultTemplate")}
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{stText("default")}</span>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] 2xl:text-xs font-medium uppercase tracking-wide text-muted-foreground block mb-1">
              {stText("description")}</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={stText("briefDescriptionOfThisTemplate")}
              disabled={isBusy}
              className="min-h-[60px] text-xs resize-none"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Dismissable hint banner */}
      {!hintDismissed && (
        <div className="relative rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2.5">
          <button
            type="button"
            onClick={dismissHint}
            className="absolute top-2 right-2 p-0.5 rounded-md text-sky-400/60 hover:text-sky-300 hover:bg-sky-500/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40"
            aria-label={stText("dismissHint")}
          >
            <Icon icon="lucide:x" className="h-3.5 w-3.5" />
          </button>
          <div className="flex gap-2.5 pr-6">
            <div className="rounded-md flex items-center justify-center h-5 w-5 shrink-0 bg-sky-500/15 mt-0.5">
              <Icon icon="lucide:info" className="h-3 w-3 text-sky-400" />
            </div>
            <div className="space-y-1 text-xs text-sky-100/80">
              <p className="font-medium text-sky-200">{stText("howTemplateFieldsWork")}</p>
              <p>
                {stText("templateFieldHelp")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary bar */}
      <div className="flex items-center gap-3 px-1 text-[10px] 2xl:text-xs text-muted-foreground">
        <span className="flex items-center gap-1 font-medium text-muted-foreground">
          <Icon icon="lucide:check-square" className="h-3 w-3 opacity-60" />
          {stText("includedFields", { count: totalIncluded })}</span>
        <span className="text-muted-foreground/25">|</span>
        <span>
          {stText("unincludedFieldsAreSkippedOnApplyExistingUserSettings")}</span>
      </div>

      {/* Field categories in mosaic layout */}
      {layout.map((item, idx) => {
        if (item.span === "full") {
          return (
            <CategorySection
              key={item.categories[0]!.id}
              category={item.categories[0]!}
              includedFields={includedFields}
              fieldValues={fieldValues}
              onToggleField={handleToggleField}
              onChangeValue={handleChangeValue}
              disabled={isBusy}
              serverType={effectiveServerType}
              availableLibraries={availableLibraries}
              contextServerType={contextServerType}
            />
          );
        }

        return (
          <div key={`row-${idx}`} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {item.categories.map((cat) => (
              <CategorySection
                key={cat.id}
                category={cat}
                includedFields={includedFields}
                fieldValues={fieldValues}
                onToggleField={handleToggleField}
                onChangeValue={handleChangeValue}
                disabled={isBusy}
                serverType={effectiveServerType}
                halfWidth={item.categories.length > 1}
                availableLibraries={availableLibraries}
                contextServerType={contextServerType}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};
